import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { flatMap as _flatMap } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DsModalService, SidebarService } from '@amfam/ui-kit';

import { KydCustomerDataModel } from '../../../../core/store/programs/kyd-smartphone/models/kyd-smartphone-policy.model';

@Component({
  selector: 'ds-kyd-smartphone-operator-table',
  templateUrl: './kyd-smartphone-operator-table.component.html',
  styleUrls: [
    './kyd-smartphone-operator-table.component.scss',
    '../../../../billing/shared/bill-accounts.scss'
  ],
  standalone: false
})
export class KydSmartphoneOperatorTableComponent implements OnInit, AfterViewInit {
  @Input() operatorsData: Dictionary<KydCustomerDataModel>;
  @Input() phoneNumbers: string[];
  @Input() isPolicyEnrolled: boolean;
  @Input() hasMultipleOperatorsEnrolled: boolean;
  @Input() isPhhEligible: boolean;
  @Input() policyState: string;
  @Input() isKydEnrolledAndEligible: boolean;
  @Output() enrollOneOperatorEvent = new EventEmitter();
  @Output() enrollMultipleOperatorEvent = new EventEmitter();
  @Output() enrollAllOperatorsEvent = new EventEmitter();
  @Output() unEnrollOneOperatorEvent = new EventEmitter();
  @Output() unEnrollMultipleOperatorEvent = new EventEmitter();
  @Output() unEnrollAllOperatorsEvent = new EventEmitter();
  @Output() updatePhoneNumberEvent = new EventEmitter();

  /**
   * editOperatorsData is an object which stores the values of items in edit mode
   **/
  public editOperatorsData: Dictionary<KydCustomerDataModel> = {};

  /**
   * submitOperatorsData is an object which stores the values of items to be submitted as payload
   **/
  public submitOperatorsData: Dictionary<KydCustomerDataModel>;

  public columns = [];
  public rows = [];
  public duplicatePhoneError = false;
  public phoneLengthError = false;
  public unEnrollOperatorName: string;
  public unEnrollOperatorId: string;

  // Handle different layout needs based on the existence of the sidebar
  sidebarPresent = false;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<any>;
  @ViewChild('driverNameCellTpl', { static: true }) driverNameCellTpl: TemplateRef<any>;
  @ViewChild('statusCellTpl', { static: true }) statusCellTpl: TemplateRef<any>;
  @ViewChild('phoneNumberCellTpl', { static: true }) phoneNumberCellTpl: TemplateRef<any>;
  @ViewChild('actionCellTpl', { static: true }) actionCellTpl: TemplateRef<any>;

  // Can see the unenroll all button
  public isEligibletoUnEnrollAll: boolean;

  // Can see the unenroll all button
  public isEligibletoEnrollAll: boolean;

  constructor(
    private sidebarService: SidebarService,
    private modalService: DsModalService
  ) {}

  ngOnInit(): void {
    this.buildTable();
  }

  ngAfterViewInit() {
    window.setTimeout(() => {
      this.sidebarService.sidebarPresent$
        .pipe(takeUntil(this.stop$))
        .subscribe(present => (this.sidebarPresent = present));
    }, 0);
  }

  /**
   *
   * @param id
   * @description When the user clicks on edit status we populate the editOperatorsData variable
   * to store the values in edit mode and not corrupt the origiinal payload.
   */
  public operatorEdit(id: string) {
    this.editOperatorsData[id] = Object.create(this.operatorsData[id]);
  }

  /**
   *
   * @param id
   * @param status
   * @description Updates the editOperatorsData with new status upon user changes.
   */
  public operatorStatusEdit(id: string, status: string) {
    this.editOperatorsData[id].status = status;
  }

  /**
   *
   * @param id
   * @param phoneNumber
   * @description Updates the editOperatorsData with phone number changes from user
   */
  public operatorPhoneNumberUpdate(id: string, phoneNumber: string) {
    const trimmedPhone = phoneNumber.replace(/-/g, '');
    this.duplicatePhoneError = this.isCurrentContextDuplicateNumberUsed(trimmedPhone);
    this.phoneLengthError = trimmedPhone.length !== 10;

    this.editOperatorsData[id].phoneNumber = trimmedPhone;
  }

  /**
   *
   * @param id
   * @description Works the same way as 'operatorEdit()' but also sets 'isEditPhone' to true,
   * which allows user to only edit phone number and not the enrollment status.
   */
  public operatorPhoneEdit(id: string) {
    this.editOperatorsData[id] = Object.create(this.operatorsData[id]);
    this.editOperatorsData[id].isEditPhone = true;
  }

  /**
   * @param id
   * @description When the user clicks on cancel we clear the item from the editOperatorsData variable
   * which was holding the value in transition.
   */
  public operatorReset(id: string) {
    this.duplicatePhoneError = false;
    this.phoneLengthError = false;
    delete this.editOperatorsData[id];
  }

  /**
   *
   * @param phoneNumber
   * @description: Check if the phone number exists in the operators data.
   */
  public isCurrentContextDuplicateNumberUsed(phoneNumber): boolean {
    const phones = _flatMap(
      Object.values(this.operatorsData),
      operator => operator.phoneNumber
    ).filter(phone => phone);
    return phones.includes(phoneNumber);
  }

  /**
   *
   * @param id
   * @description Emits an event to parent for submitting appropriate changes for operators. Handles
   * enroll/unenroll single operator and update phone number of operator
   */
  public saveOperator(id: string) {
    this.submitOperatorsData = Object.create(this.operatorsData);
    this.editOperatorsData[id].fullName = this.operatorsData[id].fullName;
    this.submitOperatorsData[id] = this.editOperatorsData[id];
    if (this.editOperatorsData[id].isEditPhone) {
      this.updatePhoneNumberEvent.emit({ operatorId: id, operators: this.submitOperatorsData });
      this.operatorReset(id);
    } else if (this.editOperatorsData[id].status === 'Enrolled') {
      this.enrollOneOperatorEvent.emit({ operatorId: id, operators: this.submitOperatorsData });
      this.operatorReset(id);
    } else {
      this.unEnrollOperatorName = this.submitOperatorsData[id].fullName;
      this.unEnrollOperatorId = id;
      this.modalService.open('unenroll-one-modal');
    }
  }

  public unenrollOne() {
    this.unEnrollOneOperatorEvent.emit({
      operatorId: this.unEnrollOperatorId,
      operators: this.submitOperatorsData
    });
    this.operatorReset(this.unEnrollOperatorId);
    this.modalService.close('unenroll-one-modal');
  }

  public enrollAll() {
    this.enrollAllOperatorsEvent.emit();
    this.editOperatorsData = {};
  }

  /**
   * @description build table for the mobile view , we get the rows from the operator data values
   */
  private buildTable() {
    this.columns = this.getColumns();
    // this.rows = this.operatorsData ? Object.values(this.operatorsData) : [];
    this.getRows();
  }

  public getRows() {
    this.rows = this.operatorsData ? Object.values(this.operatorsData) : [];
    return this.rows;
  }

  /**
   * @description build the columns to be used in the ds-table for mobile view
   */
  private getColumns() {
    return [
      this.buildTablePayload(this.driverNameCellTpl, 'Driver Name', true, 'fullName', 140),
      this.buildTablePayload(this.statusCellTpl, 'Status', false, 'status', 200),
      this.buildTablePayload(this.phoneNumberCellTpl, 'Phone Number', false, 'phoneNumber', 120),
      this.buildTablePayload(this.actionCellTpl, '', false, 'Action', 120)
    ];
  }

  private buildTablePayload(
    cellTemplateName: TemplateRef<any>,
    name: string,
    mobileHeader: boolean,
    prop: string,
    width: number
  ) {
    return {
      cellTemplate: cellTemplateName,
      headerTemplate: this.headerCellTpl,
      isHeaderOnMobile: mobileHeader,
      name: name,
      prop: prop,
      draggable: false,
      resizeable: false,
      canAutoResize: true,
      width: width
    };
  }

  public closeModal(modalId: string) {
    this.modalService.close(modalId);
    this.operatorReset(this.unEnrollOperatorId);
  }
}
